import { AfterContentInit, Component, ErrorHandler, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'

import { TunnistautuminenService } from 'app/_angular/service/tunnistautuminen.service'
import { WindowSizeService } from '../../_jaettu-angular/service/window.service'

import { Validators } from '@angular/forms'
import { disabled, FieldTree, form, metadata, REQUIRED, required } from '@angular/forms/signals'
import { TallennaUserKycRequest, TallennaUserKycResponse } from '@lemon/core-firebase/model/tunnistaminen'
import { TuettuKieli } from '@lemon/core/model/common'
import { AllowedDialogsService } from 'app/_angular/service/allowed-dialogs.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from 'app/_jaettu-angular/service/maa.service'
import { PepTekstiSharedDialog } from 'app/tervetuloa/pep-teksti-shared.dialog'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil, takeWhile } from 'rxjs/operators'

export interface TunnistautuminenNetsDialogData {
  kayttajaJoTunnistettu: boolean
  pepMissing: boolean
}

interface TunnistautujaLomake {
  nimi: string
  kansallisuus: string | null
  pep: boolean | null
}

@Component({
  templateUrl: './tunnistautuminen-nets.dialog.html',
  styleUrls: ['./tunnistautuminen-nets.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TunnistautuminenNetsDialog implements OnInit, AfterContentInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()

  readonly cellphone = signal(true)
  readonly commonError = signal<string>(undefined)

  readonly tunnistautuminenStepState = signal<'start' | 'success' | 'error' | 'error-cancel'>('start')
  readonly tunnistautujanTiedotAnnettu = signal(false)
  lomake = form(signal<TunnistautujaLomake>({ nimi: '', kansallisuus: null, pep: null }), polku => {
    required(polku.nimi)
    disabled(polku.nimi)
    required(polku.kansallisuus)
    // Signal Formsin required() hylkää arvon false (Ei), reaktiivinen Validators.required hyväksyi sen
    metadata(polku.pep, REQUIRED, () => true)
    validoiReaktiivisesti(polku.pep, Validators.required)
  })
  readonly kieli = signal<TuettuKieli>('fi')

  kansallisuudetObservable: Observable<Maa[]>

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: TunnistautuminenNetsDialogData,
    private _dialogRef: MatDialogRef<TunnistautuminenNetsDialog>,
    private _windowSizeService: WindowSizeService,
    private _allowedDialogsService: AllowedDialogsService,
    private _tunnistautuminenService: TunnistautuminenService,
    private _validationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _kayttajaService: KayttajaService,
    private _translationService: LemonTranslationService,
    private _dialog: MatDialog,
    private _maaService: MaaService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(k => {
      this.kieli.set(k || 'fi')
    })

    this.cellphone.set(window.innerWidth < 670 || window.innerHeight < 670)

    this._windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this.cellphone.set(window.innerWidth < 670 || window.innerHeight < 670)
    })

    this._dialogRef.disableClose = true

    this.tunnistautujanTiedotAnnettu.set(!this._data.pepMissing)
  }

  ngOnInit() {
    this.kansallisuudetObservable = this._maaService.kaikkiMaatObservable.pipe(
      map(kaikkiMaat => {
        const maatCopy = [...kaikkiMaat]
        // Make selecting a bit easier
        const suomi = kaikkiMaat.find(maa => maa.koodi === 'FIN')
        const ruotsi = kaikkiMaat.find(maa => maa.koodi === 'SWE')
        maatCopy.unshift({ nimi: '---', koodi: null })
        maatCopy.unshift(ruotsi)
        maatCopy.unshift(suomi)
        return maatCopy
      }),
    )

    this._kayttajaService.kayttajaObservable.pipe(takeWhile(kayttaja => !kayttaja, true)).subscribe(kayttaja => {
      asetaArvo(this.lomake.nimi, kayttaja.etunimi + ' ' + kayttaja.sukunimi)
    })

    this._dialogRef
      .beforeClosed()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        this._allowedDialogsService.allowAll()
      })
  }

  ngAfterContentInit() {
    const tunnistautuminenState = this._tunnistautuminenService.getTunnistautuminenState()
    if (tunnistautuminenState) {
      // User has returned from strong auth
      this.tunnistautuminenStepState.set(tunnistautuminenState)
      this._tunnistautuminenService.endClientSideAuthenticationSession()
    } else if (this._data.kayttajaJoTunnistettu && this._data.pepMissing) {
      // User has already completed strong auth, but is missing PEP data
      this.tunnistautuminenStepState.set('success')
    }
  }

  async tallennaTunnistautujaLisatiedot() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    // Show loading indicator
    this._ladataanService.aloitaLataaminen()

    const pyynto: TallennaUserKycRequest = {
      kansallisuus: this.lomake.kansallisuus().value(),
      pep: this.lomake.pep().value(),
    }

    return this._firebaseLemonaid
      .functionsCall<TallennaUserKycRequest, TallennaUserKycResponse>('tunnistautuminenTallennaUserKyc', pyynto)
      .then(vastaus => {
        if (vastaus?.e) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(vastaus.e)
        } else {
          this.tunnistautujanTiedotAnnettu.set(true)
        }
      })
      .catch(err => {
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
        this._errorHandler.handleError(err)
      })
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  openPepText() {
    this._dialog.open(PepTekstiSharedDialog)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  stopClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }
}
